import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import SlidePageWrapper from './SlidePageWrapper';
import Toast from './Toast';
import { securityService } from '../services/securityService';
import { useTranslation } from '../i18n/LocaleContext';

const SecurityModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('password');
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false
  });
  
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [pinForm, setPinForm] = useState({
    oldPin: '',
    newPin: '',
    confirmPin: ''
  });

  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingPin, setCheckingPin] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && activeTab === 'pin') {
      checkPinStatus();
    }
  }, [isOpen, activeTab]);

  const checkPinStatus = async () => {
    try {
      setCheckingPin(true);
      const data = await securityService.checkPinStatus();
      setHasPin(data.has_pin);
    } catch (error) {
      console.error('Check PIN status error:', error);
    } finally {
      setCheckingPin(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({ message: t('passwordMismatch'), type: 'error' });
      return;
    }
    setLoading(true);
    try {
      await securityService.changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      setToast({ message: t('passwordChanged'), type: 'success' });
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(onClose, 1200);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    
    if (pinForm.newPin !== pinForm.confirmPin) {
      setToast({ message: t('pinMismatch'), type: 'error' });
      return;
    }
    
    if (pinForm.newPin.length !== 6) {
      setToast({ message: t('pinMustBeSixDigits'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (hasPin) {
        // Đổi PIN (cần PIN cũ)
        await securityService.changePin(pinForm.oldPin, pinForm.newPin, pinForm.confirmPin);
        setToast({ message: t('pinChanged'), type: 'success' });
      } else {
        // Tạo PIN mới (lần đầu)
        await securityService.setPin(pinForm.newPin, pinForm.confirmPin);
        setToast({ message: t('pinSet'), type: 'success' });
        setHasPin(true);
      }
      
      setTimeout(() => {
        setPinForm({ oldPin: '', newPin: '', confirmPin: '' });
        onClose();
      }, 1500);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <SlidePageWrapper isOpen={isOpen} onClose={onClose} title={t('securityCenter')}>

        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 py-3 px-4 font-medium transition-colors ${
              activeTab === 'password'
                ? 'text-brand-600 border-b-2 border-brand-500'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t('changePassword')}
          </button>
          <button
            onClick={() => setActiveTab('pin')}
            className={`flex-1 py-3 px-4 font-medium transition-colors ${
              activeTab === 'pin'
                ? 'text-brand-600 border-b-2 border-brand-500'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t('withdrawals')}
          </button>
        </div>

        {activeTab === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword.old ? 'text' : 'password'}
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, oldPassword: e.target.value})}
                  className="w-full p-3 pr-12 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                  placeholder={t('password')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword({...showPassword, old: !showPassword.old})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
                >
                  {showPassword.old ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('newPassword')}</label>
              <div className="relative">
                <input
                  type={showPassword.new ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className="w-full p-3 pr-12 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                  placeholder={t('passwordMinimum')}
                  minLength="6"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword({...showPassword, new: !showPassword.new})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
                >
                  {showPassword.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('confirmPassword')}</label>
              <div className="relative">
                <input
                  type={showPassword.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full p-3 pr-12 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                  placeholder={t('passwordConfirm')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword({...showPassword, confirm: !showPassword.confirm})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
                >
                  {showPassword.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-brand-500 active:bg-brand-600 text-white text-[14px] font-bold transition-colors disabled:opacity-50"
            >
              {loading ? t('processing') : t('changePassword')}
            </button>
          </form>
        ) : checkingPin ? (
          <div className="p-8 text-center">
            <p className="text-ink-secondary">{t('loading')}</p>
          </div>
        ) : (
          <form onSubmit={handlePinSubmit} className="p-4 space-y-4">
            <div className="bg-brand-50 rounded-xl p-3 mb-4">
              <p className="text-[12.5px] text-ink-secondary">
                {t('pinInfo')}
              </p>
            </div>

            {hasPin && (
              <div>
                <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('oldPin')}</label>
                <input
                  type="password"
                  value={pinForm.oldPin}
                  onChange={(e) => setPinForm({...pinForm, oldPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                  className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-center text-2xl tracking-widest text-ink"
                  placeholder="••••••"
                  maxLength="6"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('newPin')}</label>
              <input
                type="password"
                value={pinForm.newPin}
                onChange={(e) => setPinForm({...pinForm, newPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-center text-2xl tracking-widest text-ink"
                placeholder="••••••"
                maxLength="6"
                required
              />
            </div>

            <div>
              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('confirmPin')}</label>
              <input
                type="password"
                value={pinForm.confirmPin}
                onChange={(e) => setPinForm({...pinForm, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-center text-2xl tracking-widest text-ink"
                placeholder="••••••"
                maxLength="6"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-brand-500 active:bg-brand-600 text-white text-[14px] font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('processing') : (hasPin ? t('changePinButton') : t('setPinButton'))}
            </button>
          </form>
        )}
      </SlidePageWrapper>
    </>
  );
};

export default SecurityModal;
